import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';

import CommandService from '../../Services/CommandService';
import AssetCommandHistoryDisplay from './AssetCommandHistoryDisplay';

const SORT_DIRECTIONS = ['asc', 'desc'];
const limit = 20;
let searchTimeout;

const initState = {
  dataLoading: true,
  sort: 'Date',
  order: 'desc',
  jobName: '',
  status: '',
  page: 1,
  dataCount: 0,
  data: [],
  error: '',
};

export default memo(AssetCommandHistoryContainer);

function AssetCommandHistoryContainer(props) {
  const {
    asset: { remote },
    commandExecuted,
    toggleCommandExecuted,
  } = props;
  const { assetID } = useParams();

  const [state, updateState] = useState(initState);
  const [showMobileFilters, toggleMobileFilters] = useState(false);
  const [error, setError] = useState('');
  let source = CancelToken.source();
  const commandService = new CommandService();

  const handleSortClick = () => {
    const [order] = SORT_DIRECTIONS.filter((d) => d !== state.order);
    updateCommandHistoryState({ page: 1, order });
  };

  const setPage = (page) => {
    if (page === state.page) return;
    updateCommandHistoryState({ page });
  };

  const handleStatusSelect = (status) => {
    if (status === state.status) return;
    updateCommandHistoryState({ page: 1, status });
  };

  const handleSearch = ({ target: { value: jobName } = {} } = {}) => {
    updateState({ ...state, dataLoading: true, jobName });
    window.clearTimeout(searchTimeout);
    searchTimeout = window.setTimeout(() => {
      updateCommandHistoryState({ jobName, page: 1 });
    }, 1000);
  };

  const updateCommandHistoryState = async (params) => {
    try {
      const { jobs: data = [], count: dataCount = 0 } =
        await fetchCommandHistory(params);
      updateState({
        ...state,
        data,
        dataCount,
        ...params,
        dataLoading: false,
      });
    } catch (err) {
      setError(err.message);
      updateState({ ...state, dataLoading: false });
    }
  };

  const fetchCommandHistory = async ({
    page = state.page,
    order = state.order,
    jobName = state.jobName,
    status = state.status,
  } = {}) => {
    try {
      updateState({ ...state, dataLoading: true });
      const params = { page, limit, order, jobName, status };
      const commandHistory = await commandService.fetchPaginatedCommandHistory(
        assetID,
        params,
        source.token
      );
      return commandHistory || {};
    } catch (err) {
      if (!isCancel(err)) {
        setError(err.response?.data?.message ?? err.message);
        updateState({ ...state, dataLoading: false });
      }
      return {};
    }
  };

  const _componentCleanup = () => {
    source.cancel();
    window.clearTimeout(searchTimeout);
  };

  useEffect(() => {
    if (!assetID) return;
    source = CancelToken.source();
    updateCommandHistoryState();
    if (commandExecuted) toggleCommandExecuted();
    return _componentCleanup;
  }, [assetID, commandExecuted]);

  return (
    <AssetCommandHistoryDisplay
      {...props}
      {...state}
      tab="commandHistory"
      handlePageChange={setPage}
      error={error}
      setError={setError}
      sort={{ by: 'Date', direction: state.order }}
      handleSortClick={handleSortClick}
      handleStatusSelect={handleStatusSelect}
      handleSearch={handleSearch}
      dataLoading={state.dataLoading}
      pages={Math.ceil(state.dataCount / limit)}
      remote={remote}
      showMobileFilters={showMobileFilters}
      toggleMobileFilters={toggleMobileFilters}
    />
  );
}

AssetCommandHistoryContainer.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    orgID: PropTypes.string,
    remote: PropTypes.bool,
  }),
  commandExecuted: PropTypes.bool,
  toggleCommandExecuted: PropTypes.func,
};

AssetCommandHistoryContainer.defaultProps = { asset: {} };
